/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import "../styles/PolicySwipe.css";
import MultiStepBar from "../components/MultiStepBar";
import { fetchPolicyCard } from "../api/fetchPolicyCard";
import { computeTopSim } from "../api/computeTopSim"; // Import the API function

export default function PolicySwipe() {
  const location = useLocation();
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [currentCategory, setCurrentCategory] = useState("");
  const [categoryProgress, setCategoryProgress] = useState({});
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState({});

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);

  const steps = ["Select Areas", "Swipe Cards", "See Matches"];
  const currentStep = 2; // for example
  useEffect(() => {
    // Get policy cards from location state or localStorage
    let policyCards = location.state?.policyCards;

    if (!policyCards) {
      const storedCards = localStorage.getItem("policyCards");
      if (storedCards) {
        policyCards = JSON.parse(storedCards);
      }
    }

    // If we have policy cards, use them
    if (policyCards && policyCards.length > 0) {
      setPolicies(policyCards);
      
      // Initialize progress tracking for each category
      const progress = {};
      policyCards.forEach((policy) => {
        if (!progress[policy.POLICY_AREA]) {
          progress[policy.POLICY_AREA] = {
            total: 0,
            current: 0,
          };
        }
        progress[policy.POLICY_AREA].total += 1;
      });
      
      setCategoryProgress(progress);
      setLoading(false);
    } else {
      // If no policy cards found, handle this case
      console.log("No policy cards found");
    }

    // Load selected policy areas from localStorage
    const storedAreas = localStorage.getItem("selectedPolicyAreas");
    if (storedAreas) {
      const parsedAreas = JSON.parse(storedAreas);
      setSelectedAreas(parsedAreas);
    }
    
    // Check for existing responses
    const storedResponses = localStorage.getItem('responses');
    if (storedResponses) {
      setResponses(JSON.parse(storedResponses));
    }
  }, [location]);

  // Update current category when index changes
  useEffect(() => {
    if (policies.length > 0 && currentIndex < policies.length) {
      const category = policies[currentIndex].POLICY_AREA;
      setCurrentCategory(category);
      
      // Reset all categories' current counts first
      setCategoryProgress(prev => {
        const newProgress = {...prev};
        
        // Count how many cards of each category we've gone through
        const seenCards = {};
        for (let i = 0; i <= currentIndex; i++) {
          const cardCategory = policies[i].POLICY_AREA;
          seenCards[cardCategory] = (seenCards[cardCategory] || 0) + 1;
        }
        
        // Update each category with accurate counts
        Object.keys(newProgress).forEach(cat => {
          newProgress[cat].current = seenCards[cat] || 0;
        });
        
        return newProgress;
      });
    }
  }, [policies, currentIndex]);

  const handleChoice = (choice) => {
    if (currentIndex >= policies.length) return;

    const current = policies[currentIndex];
    const updated = [...responses, { id: current.card_id, choice }];

    setResponses(updated);
    localStorage.setItem("responses", JSON.stringify(updated));

    if (currentIndex + 1 >= policies.length) {
      // All cards processed, call the API and navigate to results
      handleFinishSwiping(updated);
    } else {
      // Move to next card
      setCurrentIndex(i => i + 1);
    }
  };

  const handleSkip = () => {
    if (currentIndex + 1 >= policies.length) {
      // All cards processed, use existing responses
      handleFinishSwiping(responses);
    } else {
      // Move to next card
      setCurrentIndex(i => i + 1);
    }
  };

  const handleFinishEarly = () => {
    if (responses.length > 0) {
      handleFinishSwiping(responses);
    } else {
      // If no responses yet, show an alert
      alert("Please respond to at least one policy before seeing results.");
    }
  };

  const handleFinishSwiping = async (finalResponses) => {
    try {
      setLoading(true); // Show loading state

      // Call the API to compute top similar MPs
      const matchResults = await computeTopSim(finalResponses);

      if (!matchResults) {
        console.log(matchResults);
        throw new Error("Failed to compute matches");
      }

      // Navigate to matches page with the results
      navigate("/policy-swipe/matches", {
        state: {
          responses: finalResponses,
          matchResults: matchResults,
        },
      });
    } catch (error) {
      console.error("Error computing matches:", error);
      alert("There was a problem finding your matches. Please try again.");
      setLoading(false); // Hide loading state
    }
  };
  
  const toggleLearnMore = () => {
    setShowLearnMore(!showLearnMore);
  };

  // If no policies are loaded yet, show loading
  if (policies.length === 0) {
    return (
      <div className="swipe-container">
        <div className="loading-message">Loading policies...</div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="swipe-page">
        <nav className="navbar">{/* Your navbar code */}</nav>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Finding your political matches...</p>
        </div>
      </div>
    );
  }
  
  // Current policy to display
  const current = policies[currentIndex];
  if (!current) {
    // Handle case when there are no more cards
    return (
      <div className="swipe-container">
        <div className="no-more-cards">
          <h2>No more policy cards!</h2>
          <button
            className="btn btn-primary"
            onClick={() =>
              navigate("/policy-swipe/matches", { state: { responses } })
            }
          >
            See Your Matches
          </button>
        </div>
      </div>
    );
  }

  // Calculate progress percentage for this category
  const categoryTotal = categoryProgress[current.POLICY_AREA]?.total || 0;
  const categoryCurrent = categoryProgress[current.POLICY_AREA]?.current || 0;
  const progressPercent = categoryTotal > 0 ? (categoryCurrent / categoryTotal) * 100 : 0;

  return (
    <div className="swipe-page">
      <MultiStepBar steps={steps} currentStep={currentStep} />
      <div className="swipe-container">
        <div className="category-progress">
          <div className="category-label">
            <span className="category-name">{current.POLICY_AREA}</span>
            <span className="category-count">
              Card {categoryCurrent} of {categoryTotal}
            </span>
          </div>
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>

        <div className="swipe-policy-card swipe-card">
          <div
            className="policy-img"
            style={{
              backgroundColor: current.bgColor,
              backgroundImage: `url(${require(`../assets/${current.POLICY_AREA_ID}.jpg`)})`,
            }}
          >
            {/* <div className="policy-category">{current.POLICY_AREA}</div> */}
          </div>
          <div className="policy-content">
            <h2 className="policy-title">{current.STATEMENT}</h2>
            <p className="policy-desc">{current.DESCRIPTION}</p>

            {showLearnMore && (
              <div className="learn-more-content">
                <h3>Related Bills</h3>
                <ul className="related-bills-list">
                {current.RELATED_BILLS.map((bill, index) => (
                  <li key={index}>{bill.trim()}</li>
                ))}
              </ul>
              </div>
            )}
          </div>
        </div>

        <div className="swipe-buttons">
          <button
            className="swipe-btn dislike"
            onClick={() => handleChoice("oppose")}
            title="Oppose"
          >
            ✕
          </button>
          <button
            className="swipe-btn priority"
            onClick={() => handleChoice("priority")}
            title="Priority Support"
          >
            ★
          </button>
          <button
            className="swipe-btn like"
            onClick={() => handleChoice("support")}
            title="Support"
          >
            ✓
          </button>
        </div>

        <div className="swipe-actions">
          <button className="action-btn skip-btn" onClick={handleSkip}>
            Skip this policy
          </button>
          <button className="action-btn learn-btn" onClick={toggleLearnMore}>
            {showLearnMore ? "Hide details" : "Learn more"}
          </button>
        </div>

        <div className="finish-early">
          <button
            className="btn btn-secondary finish-btn"
            onClick={handleFinishEarly}
          >
            See Results Now
          </button>
        </div>
      </div>
    </div>
  );
}
